import Button from '@carvana/ds-button';
import { generateEvElectricLink } from '@/ev/utils/generateSrpLink';
import Image from '~/components/common/Image';
import { useDeviceType, useIsMobile } from '~/context/common/DeviceContext';
import CommonHero, { ComponentProps } from '../../common/CommonHero';

const assetsPath = `${process.env.CVNA_APP_CDN_ASSETS_PATH}/images`;
const title = 'Guide to buying a used electric vehicle';
const subheadingText = `Used electric vehicles (EVs) are more popular than ever — according to
      research from COX Automotive, 90% of all vehicle shoppers will have an
      EV on their list by 2033. If you’re in the market for a used EV now,
      you’re not alone — we’re here to help you make the best decision with
      this handy guide.`;
const heroImg = {
  largeBackground: `${assetsPath}/ev-guide-hero-background-lg.jpg`,
  largeVehicle: `${assetsPath}/ev-guide-hero-vehicles-lg.png?height=500`,
  smallBackground: `${assetsPath}/ev-guide-hero-background-sm.jpg`,
  smallVehicle: `${assetsPath}/ev-guide-hero-vehicles-sm.png?height=400`
};
const viewEvent = {
  eventName: 'Viewed - Ev Hero'
};

function CTAButton({ className }: ComponentProps) {
  const link = generateEvElectricLink();

  return (
    <a
      href={link}
      data-analytics-id="Shop Electric Vehicles"
      data-testid="shop-electric-vehicles"
    >
      <Button className={className}>Shop Electric Vehicles</Button>
    </a>
  );
}

const useImageDimensions = () => {
  const deviceType = useDeviceType();

  // Sizes come from the PNG file dimensions
  const heroRatio = {
    large: 5400 / 2127,
    small: 2301 / 1044
  };

  switch (true) {
    case deviceType.isMobile:
      const mobileHeight = 266;

      return {
        backgroundHeight: mobileHeight,
        backgroundWidth: mobileHeight * heroRatio.small
      };
    case deviceType.isTablet:
      const tabletHeight = 341;

      return {
        backgroundHeight: tabletHeight,
        backgroundWidth: tabletHeight * heroRatio.large
      };
    default:
      const desktopHeight = 547;

      return {
        backgroundHeight: desktopHeight,
        backgroundWidth: desktopHeight * heroRatio.large
      };
  }
};

function HeroImage() {
  const isMobile = useIsMobile();
  const { backgroundHeight, backgroundWidth } = useImageDimensions();

  return (
    <div className="relative col-span-full row-span-full grid">
      <Image
        src={isMobile ? heroImg.smallBackground : heroImg.largeBackground}
        alt={title}
        className="h-[266px] w-full object-cover sm:h-[341px] md:h-[600px]"
        wrapperClassName="w-full"
        height={backgroundHeight}
        width={backgroundWidth}
      />
      <img
        src={isMobile ? heroImg.smallVehicle : heroImg.largeVehicle}
        alt={title}
        className="absolute left-1/2 top-[175px] max-h-[180px] min-h-[170px] -translate-x-1/2 transform object-cover sm:top-[240px] sm:max-h-[216px] sm:min-h-[190px] md:top-[470px] md:h-[293px] md:max-h-max"
      />
    </div>
  );
}

export default function EvHero() {
  return (
    <CommonHero
      HeroImage={HeroImage}
      CTAButton={CTAButton}
      subheadingText={subheadingText}
      title={title}
      viewEvent={viewEvent}
    />
  );
}
